import axios from 'axios';
import { message } from 'antd';

/**
 * 从当前URL路径中提取公司slug
 * 匹配格式: /{companySlug}/admin..., /{companySlug}/store..., /{companySlug}/department...
 * 超管路由: /super... 无公司前缀
 */
function getCompanySlug() {
  const match = window.location.pathname.match(/^\/([^/]+)\/(admin|store|department)(\/|$)/);
  return match ? match[1] : null;
}

/**
 * 动态构建 baseURL
 * - 超管页面(/super...): /api/super
 * - 公司页面: /api/{companySlug}
 * - 公开页面: /api
 */
function getBaseURL() {
  const path = window.location.pathname;
  if (path.startsWith('/super') && (path.length === 6 || path[6] === '/')) {
    return '/api/super';
  }
  if (path.startsWith('/inspection') && (path.length === 11 || path[11] === '/')) {
    return '/api/inspection';
  }
  const slug = getCompanySlug();
  if (slug) {
    return `/api/${slug}`;
  }
  return '/api';
}

const request = axios.create({
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
request.interceptors.request.use(
  (config) => {
    // 动态设置 baseURL（每次请求都重新计算，支持路由切换）
    // 已显式指定 baseURL 的请求不覆盖（如 traceApi 固定走公开路径 /api）
    if (!config.baseURL) {
      config.baseURL = getBaseURL();
    }

    // 根据当前页面路径决定使用哪个 token
    const path = window.location.pathname;
    let token = null;
    if (path.startsWith('/super')) {
      token = localStorage.getItem('super_token');
    } else if (path.startsWith('/inspection')) {
      token = localStorage.getItem('inspection_token');
    } else if (path.match(/^\/[^/]+\/department(\/|$)/)) {
      token = localStorage.getItem('department_token');
    } else if (path.match(/^\/[^/]+\/store(\/|$)/)) {
      token = localStorage.getItem('store_token');
    } else if (path.match(/^\/[^/]+\/admin(\/|$)/)) {
      token = localStorage.getItem('admin_token');
    } else {
      // 溯源页等公开页面，尝试所有 token
      token = localStorage.getItem('admin_token') || localStorage.getItem('department_token') || localStorage.getItem('store_token');
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
request.interceptors.response.use(
  (response) => {
    const { data } = response;
    // 兼容两种返回格式: { code: 200, data: ... } 和 { success: true, data: ... }
    if (data.code && data.code !== 200) {
      message.error(data.msg || data.message || '操作失败');
      return Promise.reject(new Error(data.msg || data.message));
    }
    if (data.success === false) {
      message.error(data.message || '操作失败');
      return Promise.reject(new Error(data.message));
    }
    return data;
  },
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      const path = window.location.pathname;
      const slug = getCompanySlug();

      switch (status) {
        case 401:
          if (path.startsWith('/super')) {
            localStorage.removeItem('super_token');
            message.error('登录已过期，请重新登录');
            window.location.href = '/super/login';
          } else if (path.startsWith('/inspection')) {
            localStorage.removeItem('inspection_token');
            localStorage.removeItem('inspection_admin');
            message.error('登录已过期，请重新登录');
            window.location.href = '/inspection/login';
          } else if (path.match(/^\/[^/]+\/department(\/|$)/)) {
            localStorage.removeItem('department_token');
            localStorage.removeItem('department_admin');
            message.error('登录已过期，请重新登录');
            window.location.href = slug ? `/${slug}/department/login` : '/department/login';
          } else if (path.match(/^\/[^/]+\/store(\/|$)/)) {
            localStorage.removeItem('store_token');
            localStorage.removeItem('store_admin');
            message.error('登录已过期，请重新登录');
            window.location.href = slug ? `/${slug}/store/login` : '/store/login';
          } else {
            localStorage.removeItem('admin_token');
            message.error('登录已过期，请重新登录');
            window.location.href = slug ? `/${slug}/admin/login` : '/admin/login';
          }
          break;
        case 403:
          message.error('没有权限执行此操作');
          break;
        case 404:
          message.error(data?.message || '请求的资源不存在');
          break;
        case 500:
          message.error('服务器错误，请稍后重试');
          break;
        default:
          message.error(data?.message || '请求失败');
      }
    } else {
      message.error('网络错误，请检查网络连接');
    }
    return Promise.reject(error);
  }
);

export default request;
